/**
 * "Geosoft templates" on the Aircraft page (MB's decision 889; docs/specs/cloud/geosoft-templates.md §3; help
 * `cloud.aircraft`): the company's template Geosoft folder, one set per aircraft type (the register's type picks it,
 * decision 506): the type's QC scripts and every view they load (decision 504). Kept here once for the company and
 * copied to every Teslyn Field laptop, so Flight data QC makes a flight's database offline. Every staff member sees
 * the sets; tenant admins, managers and data processors upload a set from a Geosoft folder, or remove it. The database
 * decides (`geosoft_template_replace`, `geosoft_template_remove`).
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  AIRFRAME_LABEL,
  GEOSOFT_TEMPLATE_FAMILIES,
  QC_SCRIPT_FOLDERS,
  canEditGeosoftTemplates,
  geosoftTemplateDiff,
  geosoftTemplateDiffWords,
  geosoftTemplateFind,
  geosoftTemplatePlan,
  geosoftTemplateSet,
  geosoftTemplateSetWords,
  geosoftMissingViewWords,
  type GeosoftMissingView,
  type GeosoftTemplateFile,
  type QcFamily,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { fileBytes } from '../components/fileBytes.ts';
import {
  hashTemplateFiles,
  loadGeosoftTemplates,
  removeGeosoftTemplate,
  uploadGeosoftTemplate,
  type HashedTemplateFile,
} from './geosoftTemplatesApi.ts';

export interface GeosoftTemplatesPanelProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** The viewer's tenant roles (display only; the database decides). */
  readonly roles: readonly string[];
}

/** An upload waiting for "Upload": what it holds and changes. */
interface Pending {
  readonly family: QcFamily;
  readonly files: readonly HashedTemplateFile[];
  readonly diff: string;
  /** The name of the folder chosen, as the browser gives it. */
  readonly picked: string;
  readonly missingViews: readonly GeosoftMissingView[];
  readonly scriptZones: readonly { readonly rel_path: string; readonly zones: readonly string[] }[];
}

/** The path below the picked folder the browser gives a file (`webkitRelativePath`), else its name. */
const pickedPath = (f: File): string =>
  (f as File & { readonly webkitRelativePath?: string }).webkitRelativePath || f.name;

const latin1 = new TextDecoder('latin1');

export function GeosoftTemplatesPanel({ sb, tenantId, roles }: GeosoftTemplatesPanelProps): ReactElement {
  const [rows, setRows] = useState<GeosoftTemplateFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<QcFamily | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const editable = canEditGeosoftTemplates(roles);

  const load = useCallback(() => {
    setError(null);
    loadGeosoftTemplates(sb, tenantId).then(setRows, (e: unknown) =>
      setError(e instanceof Error ? e.message : String(e)),
    );
  }, [sb, tenantId]);
  useEffect(load, [load]);

  async function choose(family: QcFamily, chosen: readonly File[]): Promise<void> {
    if (chosen.length === 0) return;
    setError(null);
    setMessage(null);
    setPending(null);
    setBusy(family);
    try {
      const picked = chosen.map((f) => ({ path: pickedPath(f), size: f.size, file: f }));
      const found = geosoftTemplateFind(family, picked);
      if (!found.ok) throw new Error(found.message);
      const byPath = new Map(picked.map((p) => [p.path, p.file]));
      const texts = new Map<string, string>();
      for (const s of found.scripts) texts.set(s, latin1.decode(await fileBytes(byPath.get(s)!)));
      const plan = geosoftTemplatePlan(family, picked, found, texts);
      if (!plan.ok) throw new Error(plan.message);
      const files = await hashTemplateFiles(
        await Promise.all(
          plan.files.map(async (f) => ({
            rel_path: f.rel_path,
            bytes: await fileBytes(byPath.get(f.path)!),
          })),
        ),
      );
      const diff = geosoftTemplateDiff(geosoftTemplateSet(rows ?? [], family), files);
      setPending({
        family,
        files,
        diff: geosoftTemplateDiffWords(diff),
        picked: found.picked,
        missingViews: plan.missingViews,
        scriptZones: plan.scriptZones,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function upload(p: Pending): Promise<void> {
    setBusy(p.family);
    setError(null);
    try {
      const next = await uploadGeosoftTemplate(sb, tenantId, p.family, p.files, rows ?? [], (done, total) =>
        setProgress(total > 0 ? `Storing ${done} of ${total} new files…` : null),
      );
      setRows(next);
      setPending(null);
      setMessage(
        `${AIRFRAME_LABEL[p.family]}: template saved (${p.diff.replace(/\.$/, '')}). Teslyn Field laptops copy it at their next sync.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
      setProgress(null);
    }
  }

  async function remove(family: QcFamily): Promise<void> {
    const label = AIRFRAME_LABEL[family];
    if (
      !window.confirm(
        `Remove the ${label}'s Geosoft template from Teslyn Cloud? Laptops remove their copy at their next sync; projects keep the scripts already copied into them.`,
      )
    )
      return;
    setBusy(family);
    setError(null);
    setMessage(null);
    try {
      setRows(await removeGeosoftTemplate(sb, tenantId, family, rows ?? []));
      setMessage(`${label}: template removed.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel stack" aria-label="Geosoft templates">
      <h2 className="heading">Geosoft templates</h2>
      <p className="body">
        Your company&apos;s template Geosoft folder: each aircraft type&apos;s QC scripts, views and IO
        templates. Teslyn Field copies it to every laptop at each sync, and into each block&apos;s Geosoft
        folder when a project starts, offline too. Each script takes the zone of its own block&apos;s lines
        file, whatever zone it holds here. A file someone has edited in a block is never written over.
      </p>
      {!editable && (
        <p className="caption muted">
          Tenant admins, managers and data processors upload and remove templates.
        </p>
      )}
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="body" role="status">
          {message}
        </p>
      )}
      {rows === null && !error ? (
        <p className="body muted">Loading the Geosoft templates…</p>
      ) : (
        <ul className="stack" aria-label="Geosoft templates per aircraft type">
          {GEOSOFT_TEMPLATE_FAMILIES.map((family) => {
            const set = geosoftTemplateSet(rows ?? [], family);
            const label = AIRFRAME_LABEL[family];
            return (
              <li key={family} className="stack">
                <h3 className="title">{label}</h3>
                <p className="body">
                  <span className="data">Scripts\{QC_SCRIPT_FOLDERS[family]}</span>, its views and the IO
                  templates: {geosoftTemplateSetWords(rows ?? [], family)}
                </p>
                {set.length > 0 && (
                  <details>
                    <summary className="caption">Files</summary>
                    <ul aria-label={`Files of the ${label} template`}>
                      {set.map((f) => (
                        <li key={f.id} className="caption data">
                          {f.rel_path}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {editable && (
                  <div className="row">
                    <label className="field">
                      <span className="label">{`Upload the ${label}'s set from a Geosoft folder`}</span>
                      <input
                        type="file"
                        multiple
                        disabled={busy !== null}
                        // the browser asks for a folder and gives each file with its path below it
                        {...{ webkitdirectory: '', directory: '' }}
                        onChange={(e) => {
                          const chosen = Array.from(e.target.files ?? []);
                          e.target.value = '';
                          void choose(family, chosen);
                        }}
                      />
                    </label>
                    {set.length > 0 && (
                      <Button
                        variant="quiet"
                        disabled={busy !== null}
                        aria-label={`Remove the ${label} template`}
                        onClick={() => void remove(family)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                )}
                {pending?.family === family && (
                  <div className="panel stack" role="group" aria-label={`Upload the ${label} template`}>
                    <p className="body">{pending.diff}</p>
                    {pending.picked && (
                      <p className="caption">
                        From the folder you chose: <span className="data">{pending.picked}</span>
                      </p>
                    )}
                    {pending.missingViews.length > 0 && (
                      <div className="notice notice--warn stack" role="alert">
                        <p className="body">
                          Views its scripts load that are not in the folder, so left out:
                        </p>
                        <ul aria-label="Views left out">
                          {pending.missingViews.map((m) => (
                            <li key={`${m.script}:${m.line}`} className="body">
                              {geosoftMissingViewWords(m, pending.picked)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {pending.scriptZones.map((z) => (
                      <p key={z.rel_path} className="caption">
                        {`${z.rel_path} sets ${z.zones.join(', ')}. Teslyn Field replaces that with the zone of each block's lines file when it copies the script into a block.`}
                      </p>
                    ))}
                    {progress && <p className="caption muted">{progress}</p>}
                    <div className="row">
                      <Button variant="primary" busy={busy === family} onClick={() => void upload(pending)}>
                        Upload
                      </Button>
                      <Button variant="quiet" disabled={busy === family} onClick={() => setPending(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
